import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  TextField,
  Stack,
  Chip,
  Card,
  useTheme,
  useMediaQuery,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  FormHelperText,
  Grid,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import ArrayInputField from "../ArrayInputField";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import {
  IExtractSettlementDataModel,
  ISettlementDataModel,
  DuplicateActionEnumModel,
} from "../../types/types";
import { useSelector } from "react-redux";
import { RootState } from "../../store/reducers";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import dayjs, { Dayjs } from "dayjs";
import Observable from "../../utils/observable";
import { NumericFormat } from "react-number-format";

interface PreviewSettlementDialogProps {
  open: boolean;
  onClose: () => void;
  onImport: (
    data: ISettlementDataModel[],
    duplicateAction: DuplicateActionEnumModel
  ) => void;
  previewData: IExtractSettlementDataModel | undefined;
  isUploading: boolean;
  "aria-labelledby"?: string;
}

const PreviewSettlementDialog: React.FC<PreviewSettlementDialogProps> = (
  props
) => {
  const { open, onClose, onImport, previewData, isUploading } = props;
  const transactionTypes = useSelector(
    (state: RootState) => state.commonData.userData?.data?.transactionTypes
  );

  const [editedData, setEditedData] = useState<
    Observable<ISettlementDataModel>[]
  >([]);
  const [updateTrigger, setUpdateTrigger] = useState(0);
  const [duplicateAction, setDuplicateAction] =
    useState<DuplicateActionEnumModel>(DuplicateActionEnumModel.AddAsNew);
  const dataHasErrors = editedData.some((row) => {
    const hasError = {
      propertyAddress: !row.get().propertyAddress?.trim(),
      price: isNaN(row.get().price || 0),
      transactionType: !row.get().transactionTypeId,
      settlementDate: !row.get().settlementDate,
    };
    return Object.values(hasError).some((error) => error);
  });
  const isNotValid = dataHasErrors || editedData.length === 0;
  useEffect(() => {
    if (previewData?.data) {
      const observables = previewData.data.map((row) => {
        const obs = new Observable(row);
        obs.subscribe(() => setUpdateTrigger((prev) => prev + 1));
        return obs;
      });
      setEditedData(observables);
    }
  }, [previewData]);

  const errors = previewData?.errors ?? [];
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const SettlementCard = React.memo(
    ({
      row,
      index,
    }: {
      row: Observable<ISettlementDataModel>;
      index: number;
    }) => {
      const [localAddress, setLocalAddress] = useState(
        row.get().propertyAddress
      );
      const [localPrice, setLocalPrice] = useState<number | undefined>(
        row.get().price ?? undefined
      );

      const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setLocalAddress(e.target.value);
      };

      const handleBlur = () => {
        row.set({
          ...row.get(),
          propertyAddress: localAddress,
        });
      };
      const transactionTypes = useSelector(
        (state: RootState) => state.commonData.userData?.data?.transactionTypes
      );

      const hasError = {
        propertyAddress: !row.get().propertyAddress?.trim(),
        price: !(row.get().price ?? 0) || (row.get().price ?? 0) <= 0,
        transactionType: !row.get().transactionTypeId,
        settlementDate: !row.get().settlementDate,
      };
      const hasAnyError = Object.values(hasError).some((error) => error);
      return (
        <Box sx={{ position: "relative" }}>
          <Card
            sx={{
              mb: 1.5,
              p: 2,
              border: `1px solid ${
                hasAnyError ? theme.palette.error.light : theme.palette.divider
              }`,
              "&:hover": {
                borderColor: hasAnyError
                  ? theme.palette.error.main
                  : theme.palette.primary.main,
              },
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl
                  fullWidth
                  size="small"
                  error={hasError.transactionType}
                >
                  <InputLabel>Transaction Type *</InputLabel>
                  <Select
                    value={row.get().transactionTypeId || ""}
                    label="Transaction Type *"
                    onChange={(e) => {
                      row.set({
                        ...row.get(),
                        transactionTypeId: Number(e.target.value),
                      });
                    }}
                  >
                    {transactionTypes
                      ?.filter((x) => x.isSb === true)
                      .map((type) => (
                        <MenuItem key={type.id} value={type.id}>
                          {type.name}
                        </MenuItem>
                      ))}
                  </Select>
                  {hasError.transactionType && (
                    <FormHelperText>Select transaction type</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  id={`property-address-${index}-${row.get().id}`}
                  fullWidth
                  required
                  size="small"
                  label="Property Address"
                  error={hasError.propertyAddress}
                  helperText={hasError.propertyAddress && "Required field"}
                  value={localAddress || ""}
                  onChange={handleAddressChange}
                  onBlur={handleBlur}
                  inputProps={{
                    "data-testid": `property-address-${index}`,
                  }}
                />
              </Grid>

              {/* Date & Transaction Type */}
              <Grid item xs={12} md={6}>
                <DatePicker<Dayjs>
                  label="Closing Date"
                  value={
                    row.get().settlementDate
                      ? dayjs(row.get().settlementDate)
                      : null
                  }
                  onChange={(newValue: Dayjs | null) => {
                    row.set({
                      ...row.get(),
                      settlementDate: newValue ?? undefined,
                    });
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      size: "small",
                      error: hasError.settlementDate,
                      helperText: !row.get().settlementDate
                        ? "Select date"
                        : // No Status on imports: a future date makes it an Under Contract entry
                          dayjs(row.get().settlementDate).isAfter(dayjs(), "day")
                          ? "Future date: imported as Under Contract into the Pipeline folder"
                          : undefined,
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <NumericFormat
                  fullWidth
                  required
                  valueIsNumericString
                  allowLeadingZeros={false}
                  allowNegative={false}
                  customInput={TextField}
                  thousandSeparator
                  size="small"
                  label="Price"
                  decimalScale={0}
                  // type="number"
                  error={hasError.price}
                  helperText={hasError.price ? "Invalid amount" : " "}
                  value={localPrice ?? null}
                  // onChange={(e) => setLocalPrice(e.target.value)}
                  onValueChange={(values) => setLocalPrice(values.floatValue)}
                  onBlur={() => {
                    row.set({
                      ...row.get(),
                      price: localPrice,
                    });
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {row.get().transactionTypeId !== 1 && (
                <Grid item xs={12} md={6}>
                  <ArrayInputField
                    label="Buyer"
                    values={row.get().buyers || []}
                    onChange={(values) => {
                      const newData = {
                        ...row.get(),
                        buyers: values,
                      };
                      row.set(newData);
                    }}
                    placeholder="Enter buyer name"
                  />
                </Grid>
              )}

              {row.get().transactionTypeId !== 2 && (
                <Grid item xs={12} md={6}>
                  <ArrayInputField
                    label="Seller"
                    values={row.get().sellers || []}
                    onChange={(values) => {
                      const newData = {
                        ...row.get(),
                        sellers: values,
                      };
                      row.set(newData);
                    }}
                    placeholder="Enter seller name"
                  />
                </Grid>
              )}
            </Grid>
          </Card>
        </Box>
      );
    }
  );

  const columns: GridColDef[] = [
    {
      field: "propertyAddress",
      headerName: "Property Address",
      flex: 3,
      minWidth: 250,
      editable: true,
      renderEditCell: (params) => (
        <TextField
          fullWidth
          value={params.value}
          onChange={(e) =>
            params.api.setEditCellValue({
              id: params.id,
              field: params.field,
              value: e.target.value,
            })
          }
        />
      ),
    },
    {
      field: "settlementDate",
      headerName: "Settlement Date",
      flex: 2,
      minWidth: 180,
      editable: true,
      type: "string",
    },
    {
      field: "buyers",
      headerName: "Buyers",
      flex: 3,
      minWidth: 200,
      editable: true,
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          {params.value?.map((buyer: string, i: number) => (
            <Chip key={i} label={buyer} size="small" />
          ))}
        </Stack>
      ),
      renderEditCell: (params) => (
        <TextField
          fullWidth
          value={params.value?.join(", ")}
          onChange={(e) =>
            params.api.setEditCellValue({
              id: params.id,
              field: params.field,
              value: e.target.value.split(",").map((s) => s.trim()),
            })
          }
        />
      ),
    },
    {
      field: "sellers",
      headerName: "Sellers",
      flex: 3,
      minWidth: 200,
      editable: true,
      renderCell: (params) => (
        <Stack direction="column" spacing={1}>
          {params.value?.map((seller: string, i: number) => (
            <Chip key={i} label={seller} size="small" />
          ))}
        </Stack>
      ),
      renderEditCell: (params) => (
        <TextField
          fullWidth
          value={params.value?.join(", ")}
          onChange={(e) =>
            params.api.setEditCellValue({
              id: params.id,
              field: params.field,
              value: e.target.value.split(",").map((s) => s.trim()),
            })
          }
        />
      ),
    },
    {
      field: "price",
      headerName: "Price",
      flex: 2,
      minWidth: 120,
      editable: true,
      type: "number",
    },
    {
      field: "transactionTypeId",
      headerName: "Transaction Type",
      flex: 2,
      minWidth: 180,
      editable: true,
      type: "singleSelect",
      valueOptions: transactionTypes?.map((type) => ({
        value: type.id,
        label: type.name,
      })),
    },
  ];

  return (
    <Dialog
      open={open}
      maxWidth="xl"
      fullWidth
      aria-labelledby={
        props["aria-labelledby"] || "settlement-preview-dialog-title"
      }
    >
      <DialogTitle id="settlement-preview-dialog-title">
        Review Settlement Data
      </DialogTitle>
      <DialogContent>
        {errors.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }} role="alert">
            {errors.map((error, i) => (
              <Box key={i}>{error}</Box>
            ))}
          </Alert>
        )}

        {isMobile || true ? (
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              {editedData.map((row, index) => (
                <Grid item xs={12} md={12} key={index}>
                  <SettlementCard row={row} index={index} />
                </Grid>
              ))}
            </Grid>
          </Box>
        ) : (
          <Box
            sx={{
              height: "70vh",
              width: "100%",
              mt: 2,
              overflowX: "auto",
            }}
          >
            <DataGrid
              rows={editedData.map((o) => o.get())}
              columns={columns}
              processRowUpdate={(newRow, oldRow) => {
                const rowObj = editedData.find((o) => o.get().id === oldRow.id);
                if (rowObj) {
                  rowObj.set({ ...rowObj.get(), ...newRow });
                }
                return newRow;
              }}
              disableRowSelectionOnClick
              getRowId={(row) => row.id ?? ""}
              density="standard"
              autoHeight={false}
            />
          </Box>
        )}
        <Box sx={{ mt: 3, mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            How should duplicate transactions be handled?
          </Typography>
          <RadioGroup
            value={duplicateAction}
            onChange={(e) =>
              setDuplicateAction(
                Number(e.target.value) as DuplicateActionEnumModel
              )
            }
            aria-label="Duplicate handling options"
          >
            <FormControlLabel
              value={DuplicateActionEnumModel.Skip}
              control={<Radio size="small" />}
              label={
                <Typography variant="body2">
                  Ignore - Skip transactions that already exist
                </Typography>
              }
            />
            <FormControlLabel
              value={DuplicateActionEnumModel.Overwrite}
              control={<Radio size="small" />}
              label={
                <Typography variant="body2">
                  Replace - Delete duplicate transactions and replace them with
                  new uploads
                </Typography>
              }
            />
            <FormControlLabel
              value={DuplicateActionEnumModel.AddAsNew}
              control={<Radio size="small" />}
              label={
                <Typography variant="body2">
                  {/* Add as New - Import all as new records */}
                  Duplicate - Add as new, potentially creating duplicate
                  transactions
                </Typography>
              }
            />
          </RadioGroup>
        </Box>
      </DialogContent>
      {/* <Box sx={{ px: 3, mt: 2, mb: 2 }}>
        <FormControl fullWidth size="small">
          <InputLabel>Handle Duplicates</InputLabel>
          <Select
            value={duplicateAction}
            label="Handle Duplicates"
            onChange={(e) =>
              setDuplicateAction(
                Number(e.target.value) as DuplicateActionEnumModel
              )
            }
          >
            <MenuItem value={DuplicateActionEnumModel.Skip}>
              Skip (Ignore)
            </MenuItem>
            <MenuItem value={DuplicateActionEnumModel.AddAsNew}>
              Add as New
            </MenuItem>
          </Select>
          <FormHelperText>
            Select how to handle duplicate entries during import
          </FormHelperText>
        </FormControl>
      </Box> */}
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          "& .MuiButton-root": {
            minWidth: isMobile ? "100%" : 120,
            maxWidth: isMobile ? "none" : 160,
          },
        }}
      >
        <Button
          onClick={() => {
            //clear selected file

            setDuplicateAction(DuplicateActionEnumModel.Overwrite); // Reset duplicate action on close
            onClose();
          }}
          disabled={isUploading}
          sx={{ mr: 1 }}
        >
          Cancel
        </Button>
        <Button
          onClick={() => {
            onImport(
              editedData.map((o) => o.get()),
              duplicateAction!
            );
          }}
          variant="contained"
          disabled={
            isUploading ||
            editedData.length === 0 ||
            dataHasErrors ||
            duplicateAction === undefined
          }
          startIcon={isUploading ? <CircularProgress size={16} /> : null}
          aria-busy={isUploading}
          aria-label={
            dataHasErrors || duplicateAction === undefined
              ? "Please fix validation errors and select a duplicate handling option before importing"
              : `Import ${editedData.length} settlement records`
          }
        >
          {isUploading ? "Importing..." : "Import"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PreviewSettlementDialog;
